
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Shield, CheckCircle, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Product } from '@/types/portfolio';
import { CertificateModal } from './CertificateModal';

interface CertificationInformationProps {
  product: Product;
}

export const CertificationInformation = ({ product }: CertificationInformationProps) => {
  const [selectedCertification, setSelectedCertification] = useState<any>(null);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);

  // Only show for certified products
  if (product.certification_status !== "certified") {
    return null;
  }

  // Mock certification data - in a real app this would come from the database
  const certifications = [
    {
      id: 1,
      standard: "Green Seal GS-37",
      certifiedOn: "2024-01-15",
      nextVerification: "2027-01-15",
      certificateNumber: "GS-37-2024-001",
      status: "Active"
    }
  ];

  const handleViewCertificate = (certification: any) => {
    setSelectedCertification(certification);
    setIsCertificateModalOpen(true);
  };

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-600" />
            Certification Information
          </CardTitle>
          <CardDescription>Current certifications and verification status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {certifications.map((cert) => (
              <div key={cert.id} className="p-4 border rounded-lg bg-green-50/50">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <img 
                      src="/lovable-uploads/db5c9f04-b178-4abc-b8ed-9e09d1e7c292.png" 
                      alt="Green Seal Certified" 
                      className="h-10 w-auto" 
                    />
                    <div>
                      <h4 className="font-semibold text-lg">{cert.standard}</h4>
                      <Button
                        variant="link"
                        className="p-0 h-auto text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        onClick={() => handleViewCertificate(cert)}
                      >
                        Certificate #{cert.certificateNumber}
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {cert.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Certified On</p>
                      <p className="font-medium">{new Date(cert.certifiedOn).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Next Verification</p>
                      <p className="font-medium">{new Date(cert.nextVerification).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                      <p className="text-xs text-muted-foreground">
                        ({formatDistanceToNow(new Date(cert.nextVerification), { addSuffix: true })})
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Certificate Modal */}
      {selectedCertification && (
        <CertificateModal
          isOpen={isCertificateModalOpen}
          onClose={() => setIsCertificateModalOpen(false)}
          product={product}
          certification={selectedCertification}
        />
      )}
    </>
  );
};
